/**
 * Component for viewing documents associated with a case
 */
import React, { useState, useEffect } from 'react';
import { Button, Modal, Card } from '../../../shared/components';
import { fetchCaseDocuments, deleteDocument } from '../api/caseApi';
import { formatDate, formatFileSize } from '../../../shared/utils/helpers';

/**
 * Props for the ViewDocuments component
 */
interface ViewDocumentsProps {
  /**
   * ID of the case to view documents for
   */
  caseId: string;
}

/**
 * Document data structure
 */
interface Document {
  id: string;
  title: string;
  file_name: string;
  file_size: number;
  file_type: string;
  uploaded_at: string;
  uploaded_by: string;
  download_url: string;
}

/**
 * Component that displays documents associated with a case
 */
export function ViewDocuments({ caseId }: ViewDocumentsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState<string | null>(null);

  /**
   * Fetch documents when modal opens
   */
  useEffect(() => {
    if (isOpen) {
      loadDocuments();
    }
  }, [isOpen, caseId]);

  /**
   * Load documents from the API
   */
  const loadDocuments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await fetchCaseDocuments(caseId);
      setDocuments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Open the modal
   */
  const handleOpen = () => {
    setIsOpen(true);
  };

  /**
   * Close the modal
   */
  const handleClose = () => {
    setIsOpen(false);
  };

  /**
   * Handle document deletion
   */
  const handleDeleteDocument = async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) {
      return;
    }
    
    setDeleteInProgress(documentId);
    
    try {
      await deleteDocument(caseId, documentId);
      setDocuments(documents.filter(doc => doc.id !== documentId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete document');
      console.error('Error deleting document:', err);
    } finally {
      setDeleteInProgress(null);
    }
  };

  /**
   * Get icon for file type
   */
  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    } else if (fileType.includes('word') || fileType.includes('doc')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    } else if (fileType.includes('image') || fileType.includes('jpg') || fileType.includes('png')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    } else {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }
  };

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={handleOpen}
        startIcon={
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        }
      >
        View All
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Case Documents"
        size="lg"
      >
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
            <Button 
              variant="danger" 
              size="sm" 
              className="mt-2" 
              onClick={loadDocuments}
            >
              Retry
            </Button>
          </div>
        )}
        
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-8">
            <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No documents</h3>
            <p className="mt-1 text-sm text-gray-500">No documents have been uploaded to this case yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {documents.map(doc => (
              <Card
                key={doc.id}
                hoverable
                className="flex flex-col h-full"
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    {getFileIcon(doc.file_type)}
                  </div>
                  <div className="ml-4 flex-1">
                    <h4 className="text-base font-medium text-gray-900 truncate">{doc.title}</h4>
                    <p className="text-sm text-gray-500 truncate">{doc.file_name}</p>
                    <div className="mt-2 flex items-center text-xs text-gray-500">
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span className="mx-2">•</span>
                      <span>Uploaded {formatDate(doc.uploaded_at, 'dd MMM yyyy')}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 flex justify-end space-x-2">
                  <Button
                    variant="light"
                    size="sm"
                    as="a"
                    href={doc.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    }
                  >
                    Download
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeleteDocument(doc.id)}
                    isLoading={deleteInProgress === doc.id}
                    disabled={deleteInProgress === doc.id}
                    startIcon={
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    }
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
        
        <div className="mt-6 flex justify-end">
          <Button
            variant="secondary"
            onClick={handleClose}
          >
            Close
          </Button>
        </div>
      </Modal>
    </>
  );
}
